import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '@/styles/phone-input.css';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PatientCreate, Gender } from '@/client/types.gen';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const patientSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  phone: z.string().min(1, 'Phone number is required'),
  gender: z.enum(['male', 'female', 'prefer_not_to_say'] as const, {
    required_error: 'Please select a gender',
  }),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
});

type PatientFormValues = z.infer<typeof patientSchema>;

interface PatientCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateAndEdit: (data: PatientCreate) => void;
  onCreateOnly: (data: PatientCreate) => void;
}

export function PatientCreateModal({
  isOpen,
  onClose,
  onCreateAndEdit,
  onCreateOnly,
}: PatientCreateModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const form = useForm<PatientFormValues>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      gender: 'prefer_not_to_say',
      date_of_birth: '',
    },
  });

  const handleSubmit = async (data: PatientFormValues, isEdit: boolean) => {
    // Clear any previous errors
    setApiError(null);

    // Validate form before submitting
    const result = await form.trigger();
    if (!result) {
      return; // Stop if validation fails
    }

    // Convert form data to API format
    const apiData: PatientCreate = {
      name: data.name,
      phone: data.phone,
      gender: data.gender as Gender,
      date_of_birth: data.date_of_birth,
    };

    // Only include email if it's not empty
    if (data.email && data.email.trim() !== '') {
      apiData.email = data.email;
    }

    setIsSubmitting(true);
    try {
      if (isEdit) {
        await onCreateAndEdit(apiData);
      } else {
        await onCreateOnly(apiData);
        // Only reset the form after a successful API call
        form.reset({
          name: '',
          email: '',
          phone: '',
          gender: 'prefer_not_to_say',
          date_of_birth: '',
        });
      }
    } catch (error) {
      console.error('Error submitting patient data:', error);
      // Extract and display API error message if available
      if (error instanceof Error) {
        try {
          const errorData = JSON.parse(error.message);
          setApiError(errorData.detail || 'Failed to create patient');
        } catch {
          setApiError(error.message || 'Failed to create patient');
        }
      } else {
        setApiError('An unexpected error occurred');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Patient</DialogTitle>
          <DialogDescription>
            Enter the required information to register a new patient.
          </DialogDescription>
        </DialogHeader>

        {apiError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{apiError}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Patient Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="patient@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={() => (
                <FormItem>
                  <FormLabel>Phone Number *</FormLabel>
                  <FormControl>
                    <Controller
                      control={form.control}
                      name="phone"
                      render={({ field: { value, onChange } }) => (
                        <PhoneInput
                          international
                          defaultCountry="IN"
                          value={value}
                          onChange={onChange}
                          className="phone-input-dark"
                        />
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="date_of_birth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of Birth *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gender *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="prefer_not_to_say">
                        Prefer not to say
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit(form.getValues(), false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Save'
            )}
          </Button>
          <Button
            onClick={() => handleSubmit(form.getValues(), true)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Save & Continue Editing'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
